import React, { useRef, useState } from "react";
import { useForm } from "react-hook-form";
import MultiPageModal from "components/shared/modal/MultiPageModal";
import ShippingStep from "./checkout/ShippingStep";
import ReviewStep from "./checkout/ReviewStep";
import {
  deriveSteps,
  resolvePurchaserDefaults,
  buildSubmitPayload,
  RECEIVE_CHOICE
} from "helpers/bulkCheckout";

export default function BulkCheckoutModal({
  show,
  onClose,
  dropship,
  isSubmitting,
  submissionError,
  onSubmit
}) {
  const hasShippingStep = deriveSteps(dropship)[0] === "shipping";
  const [receiveChoice, setReceiveChoice] = useState(null); // optional mode only
  const [choiceError, setChoiceError] = useState(false);
  const [shipTo, setShipTo] = useState(null);
  const submitLock = useRef(false);

  const shippingForm = useForm({
    defaultValues: {
      name: "", email: "", phone: "", company: "", streetAddress: "",
      address2: "", city: "", state: "", zip: "", extra: ""
    }
  });
  const reviewForm = useForm({
    defaultValues: { po: "", name: "", email1: "", email2: "" }
  });

  // Prefill purchaser NAME + EMAIL from the ship-to contact when entering Review
  // (dirty-guarded so a manual edit is never stomped). The CONFIRM-email (email2)
  // is deliberately left blank: auto-filling a confirmation defeats its purpose.
  // Because email2 is then only ever user-typed, it can't silently drift out of
  // sync with a later ship-to email change, so no pair-guard is needed.
  const applyPurchaserPrefill = (nextShipTo) => {
    const { name, email } = resolvePurchaserDefaults(nextShipTo);
    const { dirtyFields } = reviewForm.formState;
    if (name && !dirtyFields.name) reviewForm.setValue("name", name);
    if (email && !dirtyFields.email1) reviewForm.setValue("email1", email);
  };

  // Shipping -> Review. Validates + LIFTS the ship-to values into container state
  // before the shipping fields unmount (RHF would otherwise drop them), then hands
  // the page-forward transition to MultiPageModal via the nav fn from ModalContext.
  const onShippingNext = (navigateForward) => {
    if (dropship === "optional" && receiveChoice == null) {
      setChoiceError(true);
      return;
    }
    const needAddress =
      dropship === "required" ||
      (dropship === "optional" && receiveChoice === RECEIVE_CHOICE.SHIP);

    if (needAddress) {
      shippingForm.handleSubmit((values) => {
        setShipTo(values);
        applyPurchaserPrefill(values);
        navigateForward();
      })();
    } else {
      setShipTo(null); // optional + pickup
      applyPurchaserPrefill(null);
      navigateForward();
    }
  };

  const onReviewSubmit = reviewForm.handleSubmit(async (data) => {
    if (submitLock.current || isSubmitting) return; // synchronous double-submit lock
    submitLock.current = true;
    try {
      await onSubmit(
        buildSubmitPayload({
          dropship,
          receiveChoice,
          shipTo,
          review: { po: data.po, name: data.name, email: data.email1 }
        })
      );
      // On success BulkSelection closes the wizard and startNewOrder() remounts
      // it fresh (new key) -> no field reset needed here. On failure we KEEP all
      // fields (PO included); the error surfaces via submissionError.
    } finally {
      submitLock.current = false;
    }
  });

  const isPickup =
    dropship === "optional" && receiveChoice === RECEIVE_CHOICE.PICKUP;

  return (
    <MultiPageModal
      show={show}
      onClose={onClose}
      width="600px"
      height="80vh"
      disableCloseOnOutsideClick
      closeButtonHidden={isSubmitting}
      navLocked={isSubmitting}
    >
      {hasShippingStep && (
        <ShippingStep
          title="Shipping"
          dropship={dropship}
          receiveChoice={receiveChoice}
          onReceiveChoice={(c) => {
            setReceiveChoice(c);
            setChoiceError(false);
          }}
          choiceError={choiceError}
          register={shippingForm.register}
          errors={shippingForm.formState.errors}
          onNext={onShippingNext}
        />
      )}
      <ReviewStep
        title="Place Order"
        shipTo={shipTo}
        isPickup={isPickup}
        register={reviewForm.register}
        errors={reviewForm.formState.errors}
        getValues={reviewForm.getValues}
        isSubmitting={isSubmitting}
        submissionError={submissionError}
        onSubmit={onReviewSubmit}
      />
    </MultiPageModal>
  );
}
